import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, TextField, Button, Typography, Container, Paper, Alert,
  FormControlLabel, Checkbox, useTheme, useMediaQuery, Stack,
  IconButton, InputAdornment, Fade, Slide, Avatar, Divider, Chip,
  Zoom, Card, CardContent,
} from '@mui/material';
import { login } from '../services/authService';
import { saveUserData, getUserData } from '../services/storageService';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CampaignIcon from '@mui/icons-material/Campaign';
import InsightsIcon from '@mui/icons-material/Insights';
import GroupsIcon from '@mui/icons-material/Groups';

const brand = {
  primary: '#e31e24',
  primaryDark: '#c41920',
  primaryLight: '#ff4d52',
  gray: {
    50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1',
    400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155',
    800: '#1e293b', 900: '#0f172a',
  },
};

const Login = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedUser = getUserData();
    if (savedUser) {
      setTimeout(() => {
        if (savedUser.role === 'admin') {
          navigate('/admin', { replace: true });
        } else {
          navigate('/department-selection', { replace: true });
        }
      }, 100);
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(formData.username, formData.password);
      if (result.success) {
        const userData = { ...result.data, currentDepartment: null };
        if (rememberMe) { saveUserData(userData); }
        else { localStorage.setItem('user', JSON.stringify(userData)); }
        if (result.data.role === 'admin') { navigate('/admin', { replace: true }); }
        else { navigate('/department-selection', { replace: true }); }
      } else { setError(result.error.message); }
    } catch (err) { setError('Có lỗi xảy ra. Vui lòng thử lại.'); }
    finally { setLoading(false); }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2.5,
      bgcolor: 'white',
      border: `1.5px solid ${brand.gray[200]}`,
      transition: 'all 0.2s ease',
      '& fieldset': { border: 'none' },
      '&:hover': {
        borderColor: brand.primary,
        boxShadow: `0 0 0 3px ${brand.primary}15`,
      },
      '&.Mui-focused': {
        borderColor: brand.primary,
        boxShadow: `0 0 0 3px ${brand.primary}25`,
      },
    },
    '& .MuiInputLabel-root': {
      color: brand.gray[500],
      '&.Mui-focused': { color: brand.primary },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: brand.gray[50],
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle decorative shapes */}
      <Box sx={{
        position: 'absolute', width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, ${brand.primary}08 0%, transparent 70%)`,
        top: '-10%', right: '-5%',
      }} />
      <Box sx={{
        position: 'absolute', width: 300, height: 300, borderRadius: '50%',
        background: `radial-gradient(circle, ${brand.primary}06 0%, transparent 70%)`,
        bottom: '-5%', left: '-3%',
      }} />

      <Container component="main" maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', gap: { xs: 0, md: 6 }, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
          
          {/* Left Side - Brand & Features (Desktop) */}
          {!isMobile && (
            <Fade in timeout={800}>
              <Box sx={{ flex: 1 }}>
                <Zoom in timeout={1000}>
                  <Box sx={{ mb: 5 }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 900, color: brand.gray[900], mb: 1.5,
                        fontSize: { md: '2.75rem', lg: '3.25rem' }, lineHeight: 1.2,
                      }}
                    >
                      Welcome to
                    </Typography>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 900, color: brand.primary, mb: 2,
                        fontSize: { md: '2.75rem', lg: '3.25rem' }, lineHeight: 1.2,
                      }}
                    >
                      CARNAVAL
                    </Typography>
                    <Typography variant="h6" sx={{ color: brand.gray[500], fontWeight: 400, lineHeight: 1.6 }}>
                      Quản lý livestream và công việc marketing
                    </Typography>
                  </Box>
                </Zoom>

                <Stack spacing={2}>
                  {[
                    { icon: <AssignmentIcon sx={{ fontSize: 28 }} />, title: 'Task Management', desc: 'Quản lý công việc theo Kanban, Agile', color: brand.primary },
                    { icon: <CampaignIcon sx={{ fontSize: 28 }} />, title: 'Marketing Hub', desc: 'Lên kế hoạch & quản lý content', color: '#ec4899' },
                    { icon: <InsightsIcon sx={{ fontSize: 28 }} />, title: 'Dashboard & Báo cáo', desc: 'Phân tích hiệu suất real-time', color: '#059669' },
                    { icon: <GroupsIcon sx={{ fontSize: 28 }} />, title: 'Đa phòng ban', desc: 'Marketing, Livestream, Content', color: '#f59e0b' },
                  ].map((feature, index) => (
                    <Zoom in timeout={1200 + index * 150} key={index}>
                      <Card
                        elevation={0}
                        sx={{
                          bgcolor: 'white',
                          border: `1px solid ${brand.gray[200]}`,
                          borderRadius: 3, p: 0,
                          transition: 'all 0.25s ease',
                          '&:hover': {
                            transform: 'translateX(8px)',
                            boxShadow: `0 4px 20px ${feature.color}18`,
                            borderColor: `${feature.color}40`,
                          },
                        }}
                      >
                        <CardContent sx={{ p: '14px 18px !important' }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: `${feature.color}12`, color: feature.color, width: 48, height: 48 }}>
                              {feature.icon}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" sx={{ color: brand.gray[900], fontWeight: 700 }}>
                                {feature.title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: brand.gray[500] }}>
                                {feature.desc}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Zoom>
                  ))}
                </Stack>
              </Box>
            </Fade>
          )}

          {/* Right Side - Login Form */}
          <Fade in timeout={600}>
            <Paper
              elevation={0}
              sx={{
                flex: { xs: 1, md: '0 0 440px' },
                width: { xs: '100%', md: '440px' },
                p: { xs: 4, md: 5 },
                borderRadius: 4,
                bgcolor: 'white',
                border: `1px solid ${brand.gray[200]}`,
                boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0, height: 4,
                  background: brand.primary,
                },
              }}
            >
              {/* Header */}
              <Slide direction="down" in timeout={800}>
                <Box sx={{ textAlign: 'center', mb: 4, pt: 1 }}>
                  <Zoom in timeout={1000}>
                    <Avatar
                      sx={{
                        width: { xs: 72, md: 88 }, height: { xs: 72, md: 88 },
                        bgcolor: `${brand.primary}10`,
                        mx: 'auto', mb: 2.5,
                        border: `2px solid ${brand.primary}25`,
                      }}
                    >
                      <BusinessIcon sx={{ fontSize: { xs: 36, md: 44 }, color: brand.primary }} />
                    </Avatar>
                  </Zoom>

                  <Typography
                    variant={isMobile ? 'h4' : 'h3'}
                    sx={{ fontWeight: 900, color: brand.primary, mb: 0.5, letterSpacing: '1px' }}
                  >
                    CARNAVAL
                  </Typography>

                  <Chip
                    label="Powered by Lê Đình Đài"
                    size="small"
                    sx={{
                      mb: 2, bgcolor: brand.gray[100], color: brand.gray[600],
                      fontWeight: 600, fontSize: '0.75rem',
                    }}
                  />
                  <Divider sx={{ borderColor: brand.gray[200] }} />
                </Box>
              </Slide>

              {/* Error */}
              {error && (
                <Slide direction="down" in timeout={400}>
                  <Alert
                    severity="error"
                    sx={{ mb: 3, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}
                  >
                    {error}
                  </Alert>
                </Slide>
              )}

              {/* Form */}
              <Slide direction="up" in timeout={1000}>
                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={2.5}>
                    <TextField
                      required fullWidth label="Tên đăng nhập" name="username"
                      autoComplete="username" value={formData.username}
                      onChange={handleChange} disabled={loading}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: brand.gray[400] }} /></InputAdornment>,
                      }}
                      sx={inputSx}
                    />

                    <TextField
                      required fullWidth name="password" label="Mật khẩu"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password" value={formData.password}
                      onChange={handleChange} disabled={loading}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: brand.gray[400] }} /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={loading} sx={{ color: brand.gray[400] }}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={inputSx}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          disabled={loading}
                          sx={{ color: brand.gray[400], '&.Mui-checked': { color: brand.primary } }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ color: brand.gray[600], fontWeight: 500 }}>Ghi nhớ đăng nhập (7 ngày)</Typography>}
                    />

                    <Button
                      type="submit" fullWidth variant="contained" size="large"
                      disabled={loading} startIcon={loading ? null : <LoginIcon />}
                      sx={{
                        mt: 1, py: 1.75,
                        bgcolor: loading ? brand.gray[300] : brand.primary,
                        borderRadius: 2.5, color: 'white',
                        fontSize: '1rem', fontWeight: 700, textTransform: 'none',
                        boxShadow: loading ? 'none' : `0 4px 16px ${brand.primary}40`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: loading ? brand.gray[300] : brand.primaryDark,
                          boxShadow: loading ? 'none' : `0 6px 24px ${brand.primary}50`,
                          transform: loading ? 'none' : 'translateY(-1px)',
                        },
                        '&:disabled': { bgcolor: brand.gray[300], color: brand.gray[500] },
                      }}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUpIcon sx={{ fontSize: 20, animation: 'pulse 1.5s ease-in-out infinite' }} />
                          Đang đăng nhập...
                        </Box>
                      ) : 'Đăng nhập'}
                    </Button>
                  </Stack>
                </Box>
              </Slide>
            </Paper>
          </Fade>
        </Box>

        {/* Footer */}
        <Fade in timeout={1400}>
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Chip
              icon={<BusinessIcon />}
              label="Carnaval Portal v2.0"
              sx={{
                bgcolor: 'white', border: `1px solid ${brand.gray[200]}`,
                color: brand.gray[600], fontWeight: 600, mb: 1.5,
                '& .MuiChip-icon': { color: brand.primary },
              }}
            />
            <Typography variant="body2" sx={{ color: brand.gray[500], fontWeight: 500 }}>
              © {new Date().getFullYear()} Carnaval. Designed with Lê Đình Đài.
            </Typography>
          </Box>
        </Fade>
      </Container>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(0.95); }
          }
        `}
      </style>
    </Box>
  );
};

export default Login;
